import React, {useState} from 'react';
import {Checkbox, FormControlLabel, Grid, ThemeProvider} from "@mui/material";
import {SETable} from "../../../../components/SETable";
import {RiFilter3Line, RiFilterOffLine} from "react-icons/ri";
import {SEText} from "../../../../components/SEText";
import {SEButtonExport} from "../../../../components/SEButtonExport";
import useFilterHandler from "../../../../hooks/useFilterHandler";
import {removeEmptyProperties, sortByDirection} from "../../../../utils/utils";
import {formatDateToBR, formatHoursToBRWithoutSeconds} from "../../../../../utils/time";
import useExcelExport from "../../../../hooks/useExcelExport";
import {useQuery} from "react-query";
import {tableColumns} from "./tableColumns";
import {EditarAtendimentoService} from "../services";
import {useHistory} from "react-router-dom";
import {AtendimentoInterface} from "../../../../interfaces";
import useRenderFormStore from "../../stores/useRenderFormStore";
import useFormMutation from "../../hooks/useFormMutation";
import {BaSeDialogContext} from "@base/react";
import {handleModalAdmin} from "../../../../../utils/handleModalAdmin";
import {SEButton} from "../../../../components/SEButton";
import SEInputSearch from "../../../../components/SEInputSearch";
import {cnpjMask, cpfMask} from "../../../../../utils/mask";
import theme from "../../../../mui-theme";
import SEFilterSelect from "../../../../components/SEFilterSelect";

const Tabela = () => {

    const history = useHistory();

    const {setFormData, handleShowForm} = useRenderFormStore();

    const [searchText, setSearchText] = useState<any>(null);

    const {
        paginationFilters,
        bodyFilters,
        handleResetBodyFilters,
        handleChangeBodyFilter,
        handleLinesPerPage,
        handleChangeOrder,
        handlePageChange,
        handleSearchChange,
    } = useFilterHandler();

    const handleClickApplyFilter = async (inputValue: any, oqueVocePrecisa: boolean = false) => {
        setSearchText(inputValue);
        handleSearchChange(inputValue, oqueVocePrecisa)
    }

    const {showModal} = React.useContext(BaSeDialogContext);

    const handleCleanFilters = () => {
        handleResetBodyFilters()
        setSearchText(null)
    }

    const {
        data,
        isLoading,
        refetch,
    } = useQuery({
        queryFn: () => EditarAtendimentoService.findAll(paginationFilters, bodyFilters),
        queryKey: ["editarAtendimentoData", paginationFilters, bodyFilters],
    });


    const {deleteMutation} = useFormMutation({
        showModal,
        service: EditarAtendimentoService
    });


    const handleDelete = async (id: any) => {
        handleModalAdmin({
            title: `Excluir Registro`,
            content: `Você tem certeza que deseja excluir esse registro?`,
            msgOnSuccess: `Registro excluído com sucesso`,
            onFinish: () => refetch(),
            onConfirmClick: () => deleteMutation(id),
            onCancelClick: () => console.log('modal closed...'),
            showModal: showModal,
            showAPIErrorIfAvailable: true
        });
    };


    const handleEdit = (item: AtendimentoInterface) => {
        console.log('item', item);
        const isEditing = history.location.pathname.endsWith('/edit');
        if (!isEditing) {
            setFormData(item);
            handleShowForm(true);
            history.push(`${history.location.pathname}/${item.id}/edit`)
        }
    }

    const fetchDataExcel = async () => {
        const bodyParams = removeEmptyProperties(bodyFilters);
        const pageFilter = {...paginationFilters, page: 0, linesPerPage: 1000};
        const response = await EditarAtendimentoService.findAll(pageFilter, bodyParams);
        return response?.content?.map((data: any) => ({
            COD: data.id,
            DATA: formatDateToBR(data.dataAtendimento),
            HORA: formatHoursToBRWithoutSeconds(data.dataAtendimento),
            UF: data?.uf?.sigla,
            CIDADE: data?.cidade?.nome,
            AGENTE: data?.nomeAtendente,
            CPF: cpfMask(data?.empreendedor?.respondente?.cpf ?? data?.empreendedor?.cpfPessoaResponsavel),
            CNPJ: cnpjMask(data?.empreendedor?.cnpj),
            PORTE: data?.porte?.nomePorte,
            TESTADOR: data?.treinamento ? 'Sim' : 'Não'
        }));
    };

    const {executeDownload, downloading} = useExcelExport({
        handleDataCallback: fetchDataExcel,
        filename: `Exportacao_Edicao_Registros_${formatDateToBR(new Date())}`,
        colorLoading: '#3498db',
        isCsv: true,
    });

    const {data: filterOptions} = useQuery({
        queryFn: () => EditarAtendimentoService.getOptions(true),
        queryKey: [
            "editarAtendimentoOptionsTable",
            paginationFilters,
            bodyFilters,
        ],
    });


    return (
        <Grid item>
            <Grid container>


                <Grid item xs={12}>
                    <Grid item>
                        <Grid item flexDirection="row" display="flex" alignItems="center">
                            <Grid
                                item
                                flexDirection="row"
                                display="flex"
                                alignItems="center"
                                mr={3}
                                mb={2}
                            >
                                <RiFilter3Line/>
                                <SEText as="p" size={"medium"} font="Mulish" weight="bold">
                                    Filtros
                                </SEText>
                            </Grid>
                        </Grid>
                        <ThemeProvider theme={theme}>
                            <Grid container spacing={2} my={2}>
                                <Grid item lg={3} md={2}>
                                    <SEFilterSelect
                                        defaultOption={"Todas"}
                                        id={"uf"}
                                        placeholder={"Selecione uma das opções"}
                                        label="UF"
                                        value={bodyFilters?.ufs?.[0]}
                                        options={sortByDirection(filterOptions?.ufs, 'sigla')}
                                        displayField="sigla"
                                        onChange={(value) => handleChangeBodyFilter("ufs", [value])}
                                    />
                                </Grid>

                                <Grid item lg={3} md={2}>
                                    <SEFilterSelect
                                        defaultOption={"Todos"}
                                        defaultOptionValue={""}
                                        placeholder={"Selecione uma das opções"}
                                        id={"atendentes"}
                                        label="Atendente"
                                        displayField="nome"
                                        value={bodyFilters?.atendentes}
                                        options={sortByDirection(filterOptions?.atendentes, 'nome')}
                                        onChange={(value) => {
                                            console.log('value', value);
                                            return handleChangeBodyFilter("atendentes", value)
                                        }}
                                    />
                                </Grid>

                                <Grid item lg={3} md={2}>
                                    <SEFilterSelect
                                        defaultOption={"Todos"}
                                        placeholder={"Selecione uma das opções"}
                                        id={"treinamento"}
                                        label="Testador"
                                        displayField="label"
                                        valueField={'id'}
                                        value={bodyFilters?.treinamento}
                                        options={[{label: 'Sim', id: true}, {label: 'Não', id: false}]}
                                        onChange={(value) => handleChangeBodyFilter("treinamento", value)}
                                    />
                                </Grid>

                            </Grid>
                            <Grid container spacing={2} mb={2}>
                                <Grid item>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={!!bodyFilters?.isInconsistente}
                                                onChange={(e) => handleChangeBodyFilter("isInconsistente", e.target.checked)}
                                            />
                                        }
                                        label="Com inconsistências de cadastro"
                                    />
                                </Grid>
                            </Grid>
                        </ThemeProvider>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <SETable
                        pagination={true}
                        headerTableColor={"#7B80FF"}
                        tableColor={"#FFFFFF"}
                        totalElements={data?.totalElements}
                        totalPages={data?.totalPages}
                        linesPerPageFilters={paginationFilters?.linesPerPage}
                        isLoading={isLoading}
                        data={data?.content ?? []}
                        page={paginationFilters?.page}
                        columns={tableColumns({handleEdit, handleDelete})}
                        orderBy={paginationFilters?.orderBy}
                        direction={paginationFilters?.direction}
                        onOrderChange={handleChangeOrder}
                        onPagePreviousChange={handlePageChange}
                        onPageNextChange={handlePageChange}
                        onLinesPerPageChange={handleLinesPerPage}
                        tableHeader={
                            <>
                                <Grid container justifyContent="space-between" alignItems="center" spacing={2} mb={2}>
                                    <Grid item md={3}>
                                        <SEInputSearch
                                            value={searchText}
                                            onClick={(inputValue) => handleClickApplyFilter(inputValue, false)}
                                        />
                                    </Grid>

                                    <Grid item>
                                        <SEButtonExport isLoading={downloading} onClick={executeDownload}/>
                                    </Grid>
                                </Grid>

                                <Grid container justifyContent="space-between" alignItems="center" mb={2}
                                      sx={{padding: '7px'}}>
                                    <Grid item>
                                        <div
                                            style={{
                                                display: "flex",
                                                marginTop: "16px",
                                                alignItems: "center",
                                            }}
                                        >
                                            {!!data?.totalElements && (
                                                <SEText as="p" font="Mulish">
                                                    <strong>{data?.totalElements}</strong>{" "}
                                                    {data?.totalElements === 1
                                                        ? "resultado encontrado"
                                                        : "resultados encontrados"}
                                                </SEText>
                                            )}
                                        </div>
                                    </Grid>

                                    <Grid item>
                                        <div
                                            style={{
                                                display: "flex",
                                                marginTop: "16px",
                                                alignItems: "center",
                                            }}
                                        >
                                            <SEButton onClick={handleCleanFilters}>
                                                <RiFilterOffLine size={20}/> Limpar Filtros
                                            </SEButton>
                                        </div>
                                    </Grid>
                                </Grid>
                            </>
                        }
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};

export default Tabela;